import { useEffect, useRef } from 'react';
import GiftItemShoppingCart from '@components/GiftItemShoppingCart';
import ExpandPanel from '@monofront/design-system/components/ExpandPanel';
import {
  ICartSharedContents,
  IShoppingCart,
  IShoppingCartProduct,
} from '@monofront/types';
import { fireAutoMLEvent } from '@utils/tracking';
import { useAuthentication } from '@context/businessContainer';
import { useGiftItem } from '@hooks/businessContainer';
import ShoppingCartProductRow from '@components/ShoppingCartProductRow';
import ButtonRemoveProductsFromShoppingCart from '@components/ButtonRemoveProductsFromShoppingCart';
import EmptyProducts from '@components/EmptyProducts';
import { useTranslations } from 'next-intl';
import MembershipProduct from '@components/MembershipProduct';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import ShoppingCartOOSProducts from '@components/ShoppingCartOOSProducts';
import { RichText } from 'prismic-reactjs';
import { getIsGiftItem } from '@monofront/business/utils';
import useGroupCartProductsByType from '@hooks/cart/useGroupCartProductsByType';
import HeartLoader from '@monofront/design-system/components/HeartLoader/HeartLoader';

interface IItemsProps {
  shoppingCartId: string;
  shoppingCart: IShoppingCart;
  cartSharedContents: ICartSharedContents;
}

const Items: React.FC<IItemsProps> = ({
  shoppingCartId,
  shoppingCart,
  cartSharedContents,
}) => {
  const { customer } = useAuthentication();
  const { isSubscriptionAvailable, isLoading: isSubscriptionLoading } =
    useSubscribeInCheckout(useAuthentication);
  const { giftItem, giftItemInShoppingCart } = useGiftItem();
  const autoMLTriggered = useRef<boolean>(false);

  const tCart = useTranslations('Cart');
  const tAccount = useTranslations('Account');

  const purchasableProducts = shoppingCart?.products?.filter(
    (product) => !getIsGiftItem(product) && product.inventory_quantity > 0,
  );

  const sortedProductsByType = useGroupCartProductsByType(purchasableProducts);

  useEffect(() => {
    if (
      typeof shoppingCart?.products !== 'undefined' &&
      !autoMLTriggered.current
    ) {
      fireAutoMLEvent('shopping-cart-page-view', {
        productEventDetail: {
          cartId: shoppingCartId,
          productDetails: shoppingCart?.products?.map((product) => ({
            id: product.sku,
            originalPrice: product.compare_at_price,
            displayPrice: product.price,
            currencyCode: 'EUR',
            quantity: product.quantity,
          })),
        },
      });
      autoMLTriggered.current = true;
    }
  }, [shoppingCart?.products]);

  return (
    <>
      <div className="products tw-mt-4 md:tw-mt-0">
        {isSubscriptionAvailable && !isSubscriptionLoading && (
          <div className="tw-mt-4 tw-space-y-4 tw-px-4 lg:tw-px-0">
            <MembershipProduct isImageLarge />
          </div>
        )}
        {giftItem && <GiftItemShoppingCart />}
        <ShoppingCartOOSProducts oosProducts={shoppingCart?.oosProducts} />

        {shoppingCart.shoppingCartId && (
          <>
            {purchasableProducts?.length || giftItemInShoppingCart ? (
              <div className="tw-mt-4 tw-space-y-4 tw-px-4 lg:tw-px-0">
                {sortedProductsByType.map((productType) => (
                  <ExpandPanel.Details defaultExpanded key={productType.label}>
                    <ExpandPanel.Summary UNSAFE_className="tw-rounded-md tw-bg-beige-200 tw-px-4 tw-py-3 focus:tw-outline-none">
                      <p className="tw-font-semibold">
                        {productType.label}{' '}
                        <span className="h6b">
                          ({productType.productsQuantity})
                        </span>
                      </p>
                      <ExpandPanel.ExpandIcon />
                    </ExpandPanel.Summary>
                    <div>
                      {productType.products.map(
                        (product: IShoppingCartProduct) => (
                          <div className="product-row" key={product.sku}>
                            <ShoppingCartProductRow product={product} />
                          </div>
                        ),
                      )}
                    </div>
                  </ExpandPanel.Details>
                ))}
                <div className="tw-m-4 tw-flex tw-items-center tw-justify-center">
                  <ButtonRemoveProductsFromShoppingCart
                    products={shoppingCart?.products}
                    shoppingCartId={shoppingCart.shoppingCartId}
                  />
                </div>
              </div>
            ) : cartSharedContents ? (
              <EmptyProducts
                ctaLabel={
                  (customer?.orderCount ?? 0) > 0
                    ? tAccount('myUsualProducts')
                    : tCart('iCompleteTheCart')
                }
                richText={
                  <RichText
                    render={
                      (customer?.orderCount ?? 0) > 0
                        ? cartSharedContents.primary.emptyCartWithOrdersText
                        : cartSharedContents.primary.emptyCartWithZeroOrderText
                    }
                  />
                }
              />
            ) : null}
          </>
        )}
        {!shoppingCart.shoppingCartId && <HeartLoader />}
      </div>
      <style jsx>
        {`
          .product-row {
            border-bottom: 1px solid var(--clr-tertiary-2);
          }

          .product-row:last-child {
            border-bottom: none;
          }
        `}
      </style>
    </>
  );
};

export default Items;
